// src/components/timeline/TimelineCard.tsx
//
// Phase 5E — renders a [TIMELINE] block. Horizontal scrollable
// strip of historical eras for a layered site. Each era is a
// labeled dot on a single connecting line; tap an era to expand
// it into a detail panel with summary + key features. A "Tell me
// more about X" chip at the bottom of the detail panel pushes
// the era as a chat message so the AI can drill in.
//
// Layout:
//   ┌─────────────────────────────────────────────────────────┐
//   │  Roman Forum · timeline                                 │
//   │                                                          │
//   │  ●━━━━━━●━━━━━━●━━━━━━●━━━━━━●━━━━━━●                  │
//   │  Etrus. Repub. Empire Christ. Cow F. Excav.             │
//   │                                                          │
//   │  ┌─ Republic · 509 BCE – 27 BCE ───────────────────┐    │
//   │  │ Center of Roman civic life — Senate, courts,    │    │
//   │  │ markets. The Curia Julia and Rostra date here.  │    │
//   │  │ • Curia Julia                                    │    │
//   │  │ • Rostra                                         │    │
//   │  │                                                  │    │
//   │  │ [Tell me more about the Republic →]              │    │
//   │  └─────────────────────────────────────────────────┘    │
//   └─────────────────────────────────────────────────────────┘
//
// Year formatting: negative years are displayed as "509 BCE";
// non-negative as "1803". year_end null = "to present". Open
// year_start (null) is rare but renders as "—" with the end as
// the only bound.
//
// Interaction: the timeline state is uncontrolled — tap to
// expand, tap again or tap a different era to switch. By default
// the first era is selected so something is always visible
// (rather than an empty placeholder on initial render).

import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Button,
  useTheme,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useT } from '../../i18n/useT';
import type { TimelineBlock, TimelineEra } from '../../utils/interactiveContent';

interface Props {
  block: TimelineBlock;
  /** Callback when the user taps "Tell me more about X" inside an
   *  expanded era. The parent (InteractiveContent) pipes this to
   *  the chat-message follow-up handler. */
  onEraFollowUp?: (prompt: string) => void;
}

/** Format a year for display. Negative → "509 BCE", positive →
 *  "1803". The conventional shorthand makes the timeline
 *  immediately scannable even to readers who aren't used to
 *  negative years. */
function fmtYear(y: number | null): string {
  if (y == null) return '—';
  if (y < 0) return `${Math.abs(y)} BCE`;
  return `${y}`;
}

/** Compose a year range for an era. */
function fmtRange(era: TimelineEra, t: (s: string) => string): string {
  const start = fmtYear(era.year_start);
  const end = era.year_end == null ? t('present') : fmtYear(era.year_end);
  if (era.year_start == null && era.year_end == null) return t('date unknown');
  return `${start} – ${end}`;
}

export const TimelineCard: React.FC<Props> = ({ block, onEraFollowUp }) => {
  const t = useT();
  const theme = useTheme();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const stripRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Keep the selected dot scrolled into view. Useful when an era
  // far to the right is selected via the detail panel's "next
  // era" arrow (future enhancement) or via deep-link.
  useEffect(() => {
    const dot = dotRefs.current[selectedIdx];
    if (!dot || !stripRef.current) return;
    dot.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [selectedIdx]);

  const selectedEra = block.eras[selectedIdx];

  // Stable follow-up prompt for the selected era. The AI sees a
  // message phrased as the user would naturally ask, anchored to
  // the subject so the AI has context.
  const followUpPrompt = useMemo(() => {
    if (!selectedEra) return '';
    return t('Tell me more about the {era} era of {subject}', {
      era: selectedEra.label,
      subject: block.subject,
    });
  }, [selectedEra, block.subject, t]);

  return (
    <Box
      sx={{
        my: 2,
        p: 1.75,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}
      >
        {block.subject}{' '}
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          · {t('timeline')}
        </Box>
      </Typography>

      {/* Horizontal scrollable era strip — line + dots. Each dot
          is a tappable label; the selected one is filled, others
          are outlined. The connecting line sits behind the dots
          via a pseudo-row underneath. */}
      <Box
        ref={stripRef}
        sx={{
          position: 'relative',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          // Hide scrollbar — the swipe affordance is sufficient
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'stretch',
            gap: 0,
            minWidth: '100%',
            pb: 0.5,
            // Padding-X gives the first / last dot some breathing
            // room from the edge of the scroll container.
            px: 0.5,
          }}
        >
          {/* Connecting line — runs through the vertical center
              of the dot row. Drawn as a thin div behind the dots. */}
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              right: 16,
              top: 10, // centerline of the dot
              height: 2,
              bgcolor: 'divider',
              zIndex: 0,
            }}
          />
          {block.eras.map((era, idx) => {
            const selected = idx === selectedIdx;
            return (
              <ButtonBase
                key={`${era.label}-${idx}`}
                ref={(el) => {
                  dotRefs.current[idx] = el;
                }}
                onClick={() => setSelectedIdx(idx)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flexShrink: 0,
                  px: 1,
                  minWidth: 64,
                  borderRadius: 1,
                  cursor: 'pointer',
                }}
              >
                <Box
                  sx={{
                    width: selected ? 18 : 14,
                    height: selected ? 18 : 14,
                    borderRadius: '50%',
                    bgcolor: selected
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                    border: `2px solid ${
                      selected
                        ? theme.palette.primary.main
                        : theme.palette.divider
                    }`,
                    transition: 'all 0.15s ease',
                    mb: 0.5,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: selected ? 700 : 500,
                    color: selected ? 'text.primary' : 'text.secondary',
                    fontSize: 11,
                    lineHeight: 1.2,
                    textAlign: 'center',
                    maxWidth: 90,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {era.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 10,
                    lineHeight: 1.1,
                  }}
                >
                  {era.year_start != null ? fmtYear(era.year_start) : ''}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* Detail panel — summary + features + drill-in CTA. Slides
          in via simple opacity transition on era change. */}
      {selectedEra && (
        <Box
          key={selectedIdx /* re-mount on change so transition fires */}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
            animation: 'timelineFadeIn 0.18s ease',
            '@keyframes timelineFadeIn': {
              from: { opacity: 0, transform: 'translateY(2px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 0.25 }}
          >
            {selectedEra.label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            {fmtRange(selectedEra, t)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.55, mb: 1 }}
          >
            {selectedEra.summary}
          </Typography>
          {selectedEra.key_features && selectedEra.key_features.length > 0 && (
            <Box component="ul" sx={{ pl: 2.5, my: 0.5, mb: 1.25 }}>
              {selectedEra.key_features.map((kf, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontSize: 13,
                    color: 'text.secondary',
                    lineHeight: 1.45,
                  }}
                >
                  {kf}
                </Box>
              ))}
            </Box>
          )}
          {onEraFollowUp && (
            <Button
              size="small"
              variant="text"
              endIcon={<ChevronRight />}
              onClick={() => onEraFollowUp(followUpPrompt)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'primary.main',
                px: 1,
                ml: -1,
              }}
            >
              {t('Tell me more about {era}', { era: selectedEra.label })}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
